import React, { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import { Icons } from './Icons';
import { getInitials } from '../utils/helpers';
import { useTree, useTreePeople } from '../contexts/TreeContext';
import { usePersonPhotoUrl } from '../utils/signedUrls';
import { downloadGedcom } from '../utils/exportGedcom';
import AddRelatedPersonModal from './AddRelatedPersonModal';
import ImportGedcomModal from './ImportGedcomModal';

// Card geometry constants (shared between layout and rendering)
const CARD_W = 180;       // actual card width (-90 to +90 from center)
const CARD_HALF = 90;     // horizontal half-width
const CARD_HALF_H = 40;   // vertical half-height (card height = 80, from -40 to +40)
const COUPLE_GAP = 50;    // visual gap between the two spouse cards
const SUBTREE_GAP = 50;   // horizontal gap between sibling subtrees
const V_GAP = 160;        // vertical distance between generations
const MAX_MARRIAGE_LINE = 4 * (CARD_W + COUPLE_GAP); // max centre-to-centre distance between spouses (~920px)

const SCROLL_SIZE  = 10;  // scrollbar track thickness in px
const THUMB_MIN    = 36;  // minimum thumb length in px
const TREE_PADDING = 200; // extra space around tree bounds

// Distance from couple center to each spouse center:  (CARD_W + COUPLE_GAP) / 2 = 115
const SPOUSE_OFFSET = (CARD_W + COUPLE_GAP) / 2;
// Total couple width: CARD_W * 2 + COUPLE_GAP = 410

// ── Layout engine ──────────────────────────────────────────────────────────────
export function buildLayout(people) {
  // Sorted, not just collected. Almost every downstream decision — which couple
  // is discovered first, the order of family units, which root family is placed
  // leftmost — followed the order of the keys in `people`, and those keys are
  // UUIDs, so the drawing depended on something arbitrary. The same tree could
  // render two different shapes on two clients, and a GEDCOM re-import (which
  // mints fresh ids) reliably reshuffled it.
  //
  // Oldest first, so root families come out in generational order; name and id
  // only break ties, and the id makes it total.
  const all = Object.values(people).sort((a, b) =>
    (parseInt(a.birth) || 9999) - (parseInt(b.birth) || 9999)
    || (a.name || '').localeCompare(b.name || '')
    || String(a.id).localeCompare(String(b.id))
  );
  if (all.length === 0) return { nodes: [], familyGroups: [], remainingLinks: [], couples: [] };

  // ── Step 1: collect all marriage couples ──────────────────────────────────
  const couples = [];
  const seenCouples = new Set();
  all.forEach(p => {
    (p.marriages || []).forEach(m => {
      if (!m.spouseId || !people[m.spouseId]) return;
      const key = [p.id, m.spouseId].sort().join('|');
      if (seenCouples.has(key)) return;
      seenCouples.add(key);
      // marriageDate is carried so that ordering a person's marriages is
      // possible below; nothing renders it.
      couples.push({
        a: p.id, b: m.spouseId, status: m.status || 'current',
        endReason: m.endReason, marriageDate: m.marriageDate || null,
      });
    });
  });

  // Index every person's couples for quick lookup (used in width/position calculations)
  const personCouples = {}; // personId → [coupleEntry, ...]
  couples.forEach(c => {
    if (!personCouples[c.a]) personCouples[c.a] = [];
    if (!personCouples[c.b]) personCouples[c.b] = [];
    personCouples[c.a].push(c);
    personCouples[c.b].push(c);
  });

  // ── Step 2: build nuclear family units ────────────────────────────────────
  // A family unit = { id, parents: [id1, id2?], children: [cid...] }
  const familyUnits = [];
  const personPrimaryFamily = {}; // personId → family unit where they are a parent
  const childInFamily = new Set();  // children already placed in a family unit
  const childBirthFamily = {};      // childId → family unit (birth family)
  const childHandledBy = new Set(); // `parentId|childId` — parent-child pairs captured

  // Couple-based families first
  couples.forEach(({ a, b, status, endReason, marriageDate }) => {
    const sharedChildren = all
      .filter(p => p.parentIds && p.parentIds.includes(a) && p.parentIds.includes(b))
      .sort((x, y) => (parseInt(x.birth) || 9999) - (parseInt(y.birth) || 9999))
      .map(p => p.id);

    const fu = {
      id: [a, b].sort().join('|'),
      parents: [a, b],
      children: sharedChildren,
      couple: { a, b, status, endReason, marriageDate },
    };
    familyUnits.push(fu);

    sharedChildren.forEach(cid => {
      childInFamily.add(cid);
      childBirthFamily[cid] = fu;
      childHandledBy.add(`${a}|${cid}`);
      childHandledBy.add(`${b}|${cid}`);
    });
  });

  // Choose the family each spouse is positioned *within*, now that all of them
  // exist. Only this family is walked by the recursive traversal in step 4: a
  // person's other marriages get their spouse placed alongside afterwards, and
  // those families' children fall through to the later catch-up loop, which
  // puts them wherever there is room on the row.
  //
  // This used to be "whichever couple was seen first", which for anyone with
  // more than one marriage meant an arbitrary choice. Picking a *childless*
  // marriage as the anchor was the bad case: the children of the real family
  // were then placed by the catch-up loop and could land thousands of pixels
  // from their parents, next to a different spouse entirely — a man with four
  // wives had his children drawn under the wrong one, and the tree read as if
  // she were their mother. Measured on a 129-person tree, the child sat over
  // 1000px from his own parents in 26 of 40 shuffles.
  //
  // A marriage that produced children is the one the subtree hangs off, so it
  // wins. Ties break on the earliest marriage, then the larger family, then the
  // id — all so the result cannot depend on iteration order.
  const familyRank = (fu) => [
    fu.children.length > 0 ? 0 : 1,
    fu.couple?.marriageDate || '9999',
    -fu.children.length,
    fu.id,
  ];

  familyUnits.filter(fu => fu.couple).forEach(fu => {
    fu.parents.forEach(pid => {
      const current = personPrimaryFamily[pid];
      if (!current) { personPrimaryFamily[pid] = fu; return; }
      const [a, b] = [familyRank(fu), familyRank(current)];
      for (let i = 0; i < a.length; i++) {
        if (a[i] === b[i]) continue;
        if (a[i] < b[i]) personPrimaryFamily[pid] = fu;
        return;
      }
    });
  });

  // Count how many couple families each person appears in as a parent.
  // Used below to decide whether single-parent children can be attributed to a
  // specific marriage (only safe when there's exactly one marriage).
  const personCoupleCount = {};
  familyUnits.filter(fu => fu.couple).forEach(fu => {
    fu.parents.forEach(pid => {
      personCoupleCount[pid] = (personCoupleCount[pid] || 0) + 1;
    });
  });

  // Single-parent families for remaining parent→child relationships.
  // When a parent has multiple marriages we cannot tell which marriage an
  // unattributed child belongs to.  Those families are marked isAmbiguous=true
  // and are excluded from familyGroups (so no T-connector bar is rendered);
  // instead they fall through to remainingLinks and render as curved arcs.
  // Phase 4 still uses the family unit to compute a correct position for each child.
  const singleParentFu = {}; // parentId → fu
  all.forEach(p => {
    if (childInFamily.has(p.id)) return;
    (p.parentIds || []).forEach(pid => {
      if (!people[pid] || childInFamily.has(p.id)) return;
      const isAmbiguous = (personCoupleCount[pid] || 0) > 1;
      if (!singleParentFu[pid]) {
        const fu = { id: `single|${pid}`, parents: [pid], children: [], couple: null, isAmbiguous };
        singleParentFu[pid] = fu;
        familyUnits.push(fu);
        if (!personPrimaryFamily[pid]) personPrimaryFamily[pid] = fu;
      }
      singleParentFu[pid].children.push(p.id);
      childInFamily.add(p.id);
      childBirthFamily[p.id] = singleParentFu[pid];
      // For ambiguous families do NOT add to childHandledBy — remainingLinks will
      // render a curved arc from this parent to the child instead of a T-connector.
      if (!isAmbiguous) childHandledBy.add(`${pid}|${p.id}`);
    });
  });

  // Sort single-parent children by birth year
  Object.values(singleParentFu).forEach(fu => {
    fu.children.sort((a, b) => (parseInt(people[a]?.birth) || 9999) - (parseInt(people[b]?.birth) || 9999));
  });

  // Merge single-parent families into the couple family when the lone parent is
  // one of the spouses. This handles the case where children only list one parent
  // in their parentIds — without this, those children are never positioned because
  // personPrimaryFamily for the parent is already set to the couple family, so the
  // single-parent family unit never gets walked during the tree traversal.
  //
  // Ambiguous single-parent families (parent has multiple marriages) are never
  // merged — they stay standalone for Phase 4 positioning and render as curved arcs.
  familyUnits
    .filter(fu => fu.couple)
    .forEach(couplefu => {
      // childSingleParent tracks children who belong to only one spouse (step-children,
      // or children whose parentIds only list one parent). Their T-connector drop will
      // originate from that parent's card rather than the couple midpoint.
      couplefu.childSingleParent = couplefu.childSingleParent || {};
      couplefu.parents.forEach(pid => {
        const spfu = singleParentFu[pid];
        if (!spfu || spfu.isAmbiguous) return; // ambiguous families stay standalone
        spfu.children.forEach(cid => {
          if (!couplefu.children.includes(cid)) {
            couplefu.children.push(cid);
            couplefu.childSingleParent[cid] = pid; // remember which parent this child belongs to
            childBirthFamily[cid] = couplefu;
          }
        });
        // Remove the now-redundant single-parent family unit
        const idx = familyUnits.indexOf(spfu);
        if (idx !== -1) familyUnits.splice(idx, 1);
        delete singleParentFu[pid];
      });
      // Re-sort merged children by birth year
      couplefu.children.sort((a, b) => (parseInt(people[a]?.birth) || 9999) - (parseInt(people[b]?.birth) || 9999));
    });

  // ── Step 3: compute subtree widths (memoised) ────────────────────────────
  const widthCache = {};

  // Count secondary spouses: spouses of pid that are NOT in fuParents
  function countSecondary(pid, fuParents) {
    return (personCouples[pid] || []).filter(c => {
      const other = c.a === pid ? c.b : c.a;
      return !fuParents.includes(other);
    }).length;
  }

  function getFamilyWidth(fu) {
    if (widthCache[fu.id] !== undefined) return widthCache[fu.id];
    widthCache[fu.id] = CARD_W; // sentinel — breaks cycles before recursing into children
    let parentW, shift;
    if (fu.parents.length >= 2) {
      // parents[0] is placed left, parents[1] right; secondary spouses extend the cluster
      const secLeft  = countSecondary(fu.parents[0], fu.parents);
      const secRight = countSecondary(fu.parents[1], fu.parents);
      parentW = CARD_W * 2 + COUPLE_GAP + (secLeft + secRight) * (CARD_W + COUPLE_GAP);
      // positionFamily offsets the couple by `shift` from the slot centre so the full
      // cluster (couple + secondary spouses) is centred.  Children are then centred at
      // coupleCenter = slotCenter − shift, so the slot must be wide enough to hold them.
      shift = (secRight - secLeft) * SPOUSE_OFFSET;
    } else {
      parentW = CARD_W;
      shift = 0;
    }
    if (fu.children.length === 0) return (widthCache[fu.id] = parentW);

    const childWidths = fu.children.map(cid => {
      const f = personPrimaryFamily[cid];
      return f ? getFamilyWidth(f) : CARD_W;
    });
    const totalChildW = childWidths.reduce((s, w) => s + w, 0) + (fu.children.length - 1) * SUBTREE_GAP;
    // Children centred at coupleCenter need 2*|shift| of extra room in the slot so they
    // don't bleed past the slot boundary on the side opposite the secondary spouses.
    return (widthCache[fu.id] = Math.max(parentW, totalChildW + 2 * Math.abs(shift)));
  }

  // ── Step 4: position nodes recursively ───────────────────────────────────
  const positions = {}; // personId → { x, y }
  const positionedFamilies = new Set(); // guards against circular parentId chains

  function positionPerson(id, x, y) {
    // First assignment wins — prevents re-positioning if reachable via multiple paths
    if (!positions[id]) positions[id] = { x, y };
  }

  function positionFamily(fu, centerX, y) {
    if (positionedFamilies.has(fu.id)) return;

    positionedFamilies.add(fu.id);
    // Place parents — shift the couple so the full spouse cluster (primary + secondary) is centred
    let coupleCenter = centerX;
    if (fu.parents.length >= 2) {
      const secLeft  = countSecondary(fu.parents[0], fu.parents);
      const secRight = countSecondary(fu.parents[1], fu.parents);
      // shift > 0 means cluster has more secondary spouses on the right → couple moves left
      const shift = (secRight - secLeft) * SPOUSE_OFFSET;
      coupleCenter = centerX - shift;
      positionPerson(fu.parents[0], coupleCenter - SPOUSE_OFFSET, y);
      positionPerson(fu.parents[1], coupleCenter + SPOUSE_OFFSET, y);
    } else {
      positionPerson(fu.parents[0], centerX, y);
    }

    if (fu.children.length === 0) return;

    const childY = y + V_GAP;
    const childWidths = fu.children.map(cid => {
      const f = personPrimaryFamily[cid];
      return f ? getFamilyWidth(f) : CARD_W;
    });
    const totalChildW = childWidths.reduce((s, w) => s + w, 0) + (fu.children.length - 1) * SUBTREE_GAP;
    // Children drop from the couple midpoint, not the slot centre, and step
    // aside by the least amount that clears whatever is already on the row.
    // Centred under the couple, full stop. Whatever overlap this creates is the
    // post-layout sweep's job — it separates cards by the minimum needed and
    // carries each subtree with its parent, so children stay under their own
    // parents instead of being flung to the first free gap on the row.
    let curX = coupleCenter - totalChildW / 2;

    fu.children.forEach((cid, i) => {
      const childCenter = curX + childWidths[i] / 2;
      const childFu = personPrimaryFamily[cid];
      if (childFu) {
        positionFamily(childFu, childCenter, childY);
      } else {
        positionPerson(cid, childCenter, childY);
      }
      curX += childWidths[i] + SUBTREE_GAP;
    });
  }

  // Root families: families where no parent is a child in another family
  const allChildIds = new Set(Object.keys(childBirthFamily));
  // A family earns its own slot at the top of the tree only if it is the family
  // one of its parents is positioned within. Without the second condition every
  // marriage of a multi-spouse person claimed a separate root slot, and the
  // first one processed positioned that person — so the anchor chosen above
  // could be overruled here by whichever slot came first, leaving the children
  // centred on a slot their father was not actually standing in.
  //
  // The families dropped here are not lost: their spouses are placed next to
  // their already-positioned partner by the interleaved phase below, and their
  // children by the same loop once both parents have a position. That is what
  // that pass is for.
  const topLevel = familyUnits.filter(fu => fu.parents.every(pid => !allChildIds.has(pid)));
  // Every top-level family still gets a slot — dropping any of them strands the
  // whole subtree beneath it. What changes is the ORDER: a family that is the
  // anchor of both its parents goes first, so the shared spouse of several
  // marriages is positioned inside the family that holds the children before a
  // childless marriage can claim them a slot elsewhere. Sort is stable, so
  // everything else keeps its deterministic order.
  const ranked = [...topLevel].sort((x, y) => anchorRank(x) - anchorRank(y));

  // One slot per person, not per marriage. A top-level person with several
  // marriages had every one of them handed its own slot at the top of the tree,
  // and each slot placed a spouse at its own centre — so the wives came out
  // spread 1,200px apart across the row instead of standing beside their
  // husband. The anchor marriage (first, after the ranking above) claims the
  // person; the others are dropped here and their spouse is placed adjacent by
  // the interleaved pass, which is what keeps the spouse row tight.
  const claimed = new Set();
  const rootFamilies = ranked.filter(fu => {
    if (fu.parents.some(pid => claimed.has(pid))) return false;
    fu.parents.forEach(pid => claimed.add(pid));
    return true;
  });

  function anchorRank(fu) {
    if (fu.parents.every(pid => personPrimaryFamily[pid] === fu)) return 0;
    if (fu.parents.some(pid => personPrimaryFamily[pid] === fu)) return 1;
    return 2;
  }

  // Isolated people (not in any family at all)
  const inAnyFamily = new Set([...familyUnits.flatMap(fu => fu.parents), ...allChildIds]);
  const isolated = all.filter(p => !inAnyFamily.has(p.id));

  // Place root families side by side, centred around x=500
  const rootWidths = rootFamilies.map(getFamilyWidth);
  const totalRootW = rootWidths.reduce((s, w) => s + w, 0) + Math.max(0, rootFamilies.length - 1) * SUBTREE_GAP * 2;
  let curX = 500 - totalRootW / 2;
  rootFamilies.forEach((fu, i) => {
    positionFamily(fu, curX + rootWidths[i] / 2, 80);
    curX += rootWidths[i] + SUBTREE_GAP * 2;
  });

  // Place isolated people to the right of root families
  let isoX = curX + SUBTREE_GAP;
  isolated.forEach(p => {
    if (!positions[p.id]) {
      positions[p.id] = { x: isoX, y: 80 };
      isoX += CARD_W + SUBTREE_GAP;
    }
  });

  // ── Phase 3: position extra spouses for secondary marriages ──────────────
  // Only the first-processed couple for a person gets walked in the tree traversal.
  // Extra spouses are placed adjacent to their already-positioned partner, on the
  // opposite side from the primary spouse.  When several secondary spouses share the
  // same anchor, each one steps further out so they don't stack on top of each other.

  // Helper: given a candidate x and direction (goingRight), nudge x until it no
  // longer collides with any already-positioned node at the same y level.
  function resolveCollision(candidateX, y, goingRight) {
    let x = candidateX;
    let collision = true;
    while (collision) {
      collision = false;
      for (const pos of Object.values(positions)) {
        if (!pos || Math.abs(pos.y - y) > 1) continue;
        if (Math.abs(pos.x - x) < CARD_W + COUPLE_GAP) {
          x = goingRight ? pos.x + (CARD_W + COUPLE_GAP) : pos.x - (CARD_W + COUPLE_GAP);
          collision = true;
          break;
        }
      }
    }
    return x;
  }

  // ── Phase 3 + 4 (interleaved) ─────────────────────────────────────────────
  // Phase 3 and Phase 4 must run together in a single loop so that spouses of
  // people placed by Phase 4 are correctly positioned at the right Y level
  // rather than falling through to the safety fallback row.
  //
  // Each iteration:
  //   - Phase 3: place any unpositioned spouse adjacent to their placed partner
  //   - Phase 4: place children of any family whose parents are all now placed
  // Repeat until nothing new is placed.
  // Spouses stay beside the person they married.
  //
  // This used to push a second spouse clear of every child of the first
  // marriage, on the reasoning that her connector would otherwise cross them.
  // The clearance is only bearable when the other family is small: those
  // children carry their own descendants, so the block can be tens of thousands
  // of pixels wide, and the spouse was thrown that far out. On a real tree it
  // put three of a man's four wives ~7,000px away, with marriage lines drawn
  // across the whole generation and the first wife's outermost children sitting
  // under a different wife entirely — which is exactly how someone reads the
  // wrong mother off the picture.
  //
  // Each marriage's children are now placed under their own parent instead
  // (see the child placement below), so the crossing this guarded against does
  // not arise, and the spouse can stay where she belongs: next to her husband,
  // within MAX_MARRIAGE_LINE.
  function enforceSpousePosition(anchorId, spouseId, anchorPosY, x, goingRight) {
    return x;
  }

  let anyChanged = true;
  while (anyChanged) {
    anyChanged = false;

    // ── Phase 3: secondary spouse placement ───────────────────────────────
    couples.forEach(({ a, b }) => {
      const posA = positions[a];
      const posB = positions[b];

      if (posA && !posB) {
        // Place b on the correct side of a.
        const primaryFu  = personPrimaryFamily[a];
        const primarySid = primaryFu?.parents.find(pid => pid !== a);
        const primaryX   = primarySid ? positions[primarySid]?.x : undefined;
        const secXs = (personCouples[a] || [])
          .map(c => c.a === a ? c.b : c.a)
          .filter(sid => sid !== b && sid !== primarySid && positions[sid])
          .map(sid => positions[sid].x);
        let x, goingRight;
        if (primaryX !== undefined && primaryX > posA.x) {
          const anchor = secXs.length > 0 ? Math.min(...secXs) : posA.x;
          x = anchor - (CARD_W + COUPLE_GAP);
          goingRight = false;
        } else {
          const anchor = secXs.length > 0 ? Math.max(...secXs) : posA.x;
          x = anchor + (CARD_W + COUPLE_GAP);
          goingRight = true;
        }
        x = enforceSpousePosition(a, b, posA.y, x, goingRight);
        const xFloorA = x; // minimum safe position (spouse must stay outside sibling children)
        x = resolveCollision(x, posA.y, goingRight);
        // Cap marriage line length; never pull spouse back inside the sibling-children range.
        if (goingRight) {
          const xCapMax = posA.x + MAX_MARRIAGE_LINE;
          if (xCapMax >= xFloorA) x = Math.min(x, xCapMax);
        } else {
          const xCapMin = posA.x - MAX_MARRIAGE_LINE;
          if (xCapMin <= xFloorA) x = Math.max(x, xCapMin);
        }
        positions[b] = { x, y: posA.y };
        anyChanged = true;
      } else if (posB && !posA) {
        const primaryFu  = personPrimaryFamily[b];
        const primarySid = primaryFu?.parents.find(pid => pid !== b);
        const primaryX   = primarySid ? positions[primarySid]?.x : undefined;
        const secXs = (personCouples[b] || [])
          .map(c => c.a === b ? c.b : c.a)
          .filter(sid => sid !== a && sid !== primarySid && positions[sid])
          .map(sid => positions[sid].x);
        let x, goingRight;
        if (primaryX !== undefined && primaryX > posB.x) {
          const anchor = secXs.length > 0 ? Math.min(...secXs) : posB.x;
          x = anchor - (CARD_W + COUPLE_GAP);
          goingRight = false;
        } else {
          const anchor = secXs.length > 0 ? Math.max(...secXs) : posB.x;
          x = anchor + (CARD_W + COUPLE_GAP);
          goingRight = true;
        }
        x = enforceSpousePosition(b, a, posB.y, x, goingRight);
        const xFloorB = x;
        x = resolveCollision(x, posB.y, goingRight);
        if (goingRight) {
          const xCapMax = posB.x + MAX_MARRIAGE_LINE;
          if (xCapMax >= xFloorB) x = Math.min(x, xCapMax);
        } else {
          const xCapMin = posB.x - MAX_MARRIAGE_LINE;
          if (xCapMin <= xFloorB) x = Math.max(x, xCapMin);
        }
        positions[a] = { x, y: posB.y };
        anyChanged = true;
      }
    });

    // ── Phase 4: children of families the recursion did not reach ─────────
    // A single pass now. It used to run twice, so that families whose siblings
    // were already on the row could be placed "outside" that block first — a
    // detail of the exile rule that no longer exists, since each marriage's
    // children now hang under their own parent regardless of what else is there.
    familyUnits.forEach(fu => {
      if (fu.children.length === 0) return;
      if (!fu.parents.every(pid => positions[pid])) return;
      if (fu.children.every(cid => positions[cid])) return;

      const parentY = positions[fu.parents[0]].y;
      const childY  = parentY + V_GAP;

      const childWidths = fu.children.map(cid => {
        const f = personPrimaryFamily[cid];
        return f ? getFamilyWidth(f) : CARD_W;
      });
      const totalChildW = childWidths.reduce((s, w) => s + w, 0) + (fu.children.length - 1) * SUBTREE_GAP;

      // Where this family's children hang.
      //
      // For a person's one and only marriage, that is the couple midpoint, as
      // always. But when one parent is shared with another marriage, every
      // marriage would want the same midpoint — the shared parent's — and the
      // two sets of children would pile onto the same spot. So a shared
      // marriage hangs its children under the *other* parent instead: the wife
      // whose children they are. Each set then sits under its own mother, which
      // is the whole point, and the connector drops from her card too.
      const sharedParentId = fu.parents.find(pid =>
        familyUnits.some(ofu => ofu !== fu && ofu.couple && ofu.parents.includes(pid))
      );
      const secondarySpouseId = sharedParentId
        ? fu.parents.find(pid => pid !== sharedParentId)
        : null;

      const anchorX = secondarySpouseId && positions[secondarySpouseId]
        ? positions[secondarySpouseId].x
        : fu.parents.reduce((s, pid) => s + positions[pid].x, 0) / fu.parents.length;
      const startX = anchorX - totalChildW / 2;

      // Drop the connector from that parent's card rather than the couple
      // midpoint, so two families under one person stay distinguishable.
      if (secondarySpouseId) fu.dropParentId = secondarySpouseId;

      let curX = startX;
      fu.children.forEach((cid, i) => {
        const childCenter = curX + childWidths[i] / 2;
        if (!positions[cid]) {
          const childFu = personPrimaryFamily[cid];
          if (childFu) positionFamily(childFu, childCenter, childY);
          else         positionPerson(cid, childCenter, childY);
          anyChanged = true;
        }
        curX += childWidths[i] + SUBTREE_GAP;
      });
    });
  }

  // Safety fallback — also catches NaN coordinates, which make SVG elements invisible
  all.forEach((p, i) => {
    const pos = positions[p.id];
    if (!pos || !isFinite(pos.x) || !isFinite(pos.y)) {
      positions[p.id] = { x: 500 + i * (CARD_W + SUBTREE_GAP), y: 80 };
    }
  });

  // ── Post-layout overlap resolution ────────────────────────────────────────
  // After all placement phases, sweep each generation row top→bottom, left→right
  // and push cards apart if their centres are closer than one card-width + gap.
  // When a node is pushed right its entire descendant subtree moves with it so
  // that parent↔child connectors remain visually aligned.
  // Critically: if the pushed position would land BETWEEN a married couple,
  // skip past the couple entirely so the marriage line doesn't pass through
  // the moved card (which would make it look like a false marriage connection).
  const MIN_NODE_SPACING = CARD_W + COUPLE_GAP; // 230px centre-to-centre

  // Build a per-row list of couple x-ranges for fast sandwich detection
  const coupleRangesByRow = {};
  couples.forEach(c => {
    const pa = positions[c.a], pb = positions[c.b];
    if (!pa || !pb || Math.abs(pa.y - pb.y) > 5) return;
    const rowY = Math.round(pa.y);
    if (!coupleRangesByRow[rowY]) coupleRangesByRow[rowY] = [];
    coupleRangesByRow[rowY].push({ leftX: Math.min(pa.x, pb.x), rightX: Math.max(pa.x, pb.x), a: c.a, b: c.b });
  });

  // personId → the people they are married to, for the co-spouse check below.
  const spouseSets = {};
  couples.forEach(c => {
    (spouseSets[c.a] = spouseSets[c.a] || new Set()).add(c.b);
    (spouseSets[c.b] = spouseSets[c.b] || new Set()).add(c.a);
  });

  function skipPastCouples(x, rowY, nodeId) {
    const ranges = coupleRangesByRow[rowY] || [];
    let resolved = x;
    let changed = true;
    while (changed) {
      changed = false;
      for (const r of ranges) {
        if (r.a === nodeId || r.b === nodeId) continue; // own couple — fine to be inside
        // Co-spouses are fine to be inside too. A second wife necessarily stands
        // between her husband and his other wives, so every one of those couple
        // ranges contains her. Treating that as a sandwich pushed each wife past
        // all the others in turn — four wives ended up 1,200px apart in a row
        // that should have been shoulder to shoulder, undoing the placement.
        if (spouseSets[nodeId]?.has(r.a) || spouseSets[nodeId]?.has(r.b)) continue;
        if (resolved > r.leftX && resolved < r.rightX) {
          resolved = r.rightX + MIN_NODE_SPACING;
          changed = true;
          break;
        }
      }
    }
    return resolved;
  }

  // When a node is pushed right, cascade the same delta to all of its descendants
  // (children, grandchildren, …) so their connector lines stay aligned with the
  // parent card.  Only descends through the children axis — does NOT follow
  // spouses into their own other families, which would shift unrelated subtrees.
  function shiftSubtreeRight(rootId, delta) {
    if (delta <= 0) return;
    const visited = new Set();
    const queue = [rootId];
    while (queue.length) {
      const id = queue.shift();
      if (visited.has(id)) continue;
      visited.add(id);
      if (positions[id]) positions[id].x += delta;
      familyUnits
        .filter(f => f.parents.includes(id))
        .forEach(f => f.children.forEach(cid => {
          if (!visited.has(cid)) queue.push(cid);
        }));
    }
  }

  const rowBuckets = {};
  all.forEach(p => {
    const rowY = Math.round(positions[p.id].y);
    if (!rowBuckets[rowY]) rowBuckets[rowY] = [];
    rowBuckets[rowY].push(p.id);
  });
  // Process rows top-to-bottom so cascaded shifts from upper rows are already
  // applied before lower rows are evaluated, preventing double-pushes.
  Object.entries(rowBuckets)
    .sort(([ya], [yb]) => Number(ya) - Number(yb))
    .forEach(([, ids]) => {
      if (ids.length < 2) return;
      ids.sort((a, b) => positions[a].x - positions[b].x);
      const rowY = Math.round(positions[ids[0]].y);
      for (let i = 1; i < ids.length; i++) {
        const minX = positions[ids[i - 1]].x + MIN_NODE_SPACING;
        if (positions[ids[i]].x < minX) {
          const newX = skipPastCouples(minX, rowY, ids[i]);
          shiftSubtreeRight(ids[i], newX - positions[ids[i]].x);
        }
      }
    });

  // ── Assign distinct colors to families that share a parent with another couple ──
  // Colors are assigned from a global counter so two unrelated multi-spouse situations
  // never accidentally share a color. Colors then propagate to all descendant families
  // so the lineage is visually traceable across generations.
  const MULTI_FAMILY_COLORS = ['#B07A3A', '#4D86A0', '#5E9E5A', '#9E5E9E', '#A04848', '#40A090', '#8A7A30', '#7060A8'];
  let colorIndex = 0;
  const parentToCoupleFamilies = {}; // personId → [familyUnit, ...]
  familyUnits.filter(fu => fu.couple).forEach(fu => {
    fu.parents.forEach(pid => {
      if (!parentToCoupleFamilies[pid]) parentToCoupleFamilies[pid] = [];
      if (!parentToCoupleFamilies[pid].includes(fu)) parentToCoupleFamilies[pid].push(fu);
    });
  });
  Object.values(parentToCoupleFamilies).forEach(fus => {
    if (fus.length <= 1) return; // no ambiguity, leave default color
    fus.forEach(fu => {
      if (!fu.color) fu.color = MULTI_FAMILY_COLORS[colorIndex++ % MULTI_FAMILY_COLORS.length];
    });
  });

  // ── Propagate lineage colors to descendant families ───────────────────────
  // A child born into a colored family inherits that color; when they become a
  // parent their own family's connectors use the same color, passing it further.
  const personLineageColor = {};
  familyUnits.filter(fu => fu.color).forEach(fu => {
    fu.children.forEach(cid => { personLineageColor[cid] = fu.color; });
  });
  for (let iter = 0; iter < 50; iter++) {
    let progress = false;
    familyUnits.forEach(fu => {
      if (fu.color || fu.children.length === 0) return;
      const parentColor = fu.parents.map(pid => personLineageColor[pid]).find(c => c);
      if (!parentColor) return;
      fu.color = parentColor;
      fu.children.forEach(cid => { personLineageColor[cid] = parentColor; });
      progress = true;
    });
    if (!progress) break;
  }

  // ── Assemble output ──────────────────────────────────────────────────────
  const nodes = all.map(p => ({ id: p.id, ...positions[p.id] }));

  // All family units with children become T-connector groups, except ambiguous
  // single-parent families (parent has multiple marriages) which render as curved arcs.
  const familyGroups = familyUnits
    .filter(fu => fu.children.length > 0 && !fu.isAmbiguous)
    .map(fu => ({ parents: fu.parents, children: fu.children, childSingleParent: fu.childSingleParent || {}, color: fu.color || null, dropParentId: fu.dropParentId || null }));

  // Any parent→child not captured by a family unit (edge case)
  const remainingLinks = [];
  all.forEach(p => {
    (p.parentIds || []).forEach(pid => {
      if (people[pid] && !childHandledBy.has(`${pid}|${p.id}`)) {
        remainingLinks.push({ from: pid, to: p.id });
      }
    });
  });

  return { nodes, familyGroups, remainingLinks, couples };
}

// ── Single person card ─────────────────────────────────────────────────────
// React.memo: with incremental docChanges() updates, unchanged person objects keep
// their reference so this card won't re-render unless its person's data actually changed.
const TreePersonCard = React.memo(function TreePersonCard({ person, x, y, isSelected, onSelect, isRoot }) {
  const alive = !person.death;
  // 42px: the avatar circle below. Every card on screen renders one of these, so
  // this is the call that decides whether opening a tree costs kilobytes or
  // megabytes.
  const photoUrl = usePersonPhotoUrl(person, 42);
  return (
    <g
      transform={`translate(${x}, ${y})`}
      onClick={(e) => { e.stopPropagation(); onSelect(person.id); }}
      style={{ cursor: 'pointer' }}
    >
      <rect
        x={-90} y={-40} width={180} height={80} rx={12}
        fill={isSelected ? '#3B2F1E' : person.gender === 'F' ? '#F4EEF0' : '#EEF0F4'}
        stroke={isSelected ? '#C4975A' : '#D5CAB8'}
        strokeWidth={isSelected ? 2.5 : 1.5}
      />
      {isRoot && <rect x={-90} y={-40} width={180} height={4} rx={2} fill="#C4975A" />}
      {/* Avatar */}
      <circle
        cx={-52} cy={0} r={22}
        fill={person.gender === 'F' ? '#DBC8CE' : '#C8CEDB'}
        stroke={isSelected ? '#C4975A' : '#B8AFA0'}
        strokeWidth={1.5}
      />
      {photoUrl ? (
        <clipPath id={`clip-${person.id}`}><circle cx={-52} cy={0} r={21} /></clipPath>
      ) : null}
      {photoUrl ? (
        <image
          href={photoUrl}
          x={-73} y={-21} width={42} height={42}
          clipPath={`url(#clip-${person.id})`}
          preserveAspectRatio="xMidYMid slice"
        />
      ) : (
        <text x={-52} y={5} textAnchor="middle" fontSize="12" fontWeight="700"
          fill={isSelected ? '#F5EDE3' : '#6B5E4F'} fontFamily="'Crimson Pro', Georgia, serif">
          {getInitials(person.name)}
        </text>
      )}
      {/* Name */}
      <text x={-20} y={-10} fontSize="12.5" fontWeight="600"
        fill={isSelected ? '#F5EDE3' : '#2C2418'} fontFamily="'Crimson Pro', Georgia, serif">
        {(person.name || '').split(' ')[0]} {(person.name || '').split(' ').slice(-1)}
      </text>
      {/* Dates */}
      <text x={-20} y={7} fontSize="10.5"
        fill={isSelected ? '#C4975A' : '#8B7D6B'} fontFamily="'DM Sans', sans-serif">
        {person.birth?.slice(0, 4) || '?'}–{person.death ? person.death.slice(0, 4) : 'present'}
      </text>
      {/* Living indicator */}
      {alive && <circle cx={72} cy={-28} r={5} fill="#6DAE72" stroke="#fff" strokeWidth={1.5} />}
      {/* Occupation */}
      <text x={-20} y={22} fontSize="9.5"
        fill={isSelected ? '#A89474' : '#A0937F'} fontFamily="'DM Sans', sans-serif">
        {person.occupation || ''}
      </text>
    </g>
  );
});

// ── Relationship add buttons for the selected card ─────────────────────────
const BTN_R      = 13;   // circle radius
const BTN_OFFSET = 20;   // gap from card edge to button center

function RelationshipButtons({ node, person, people, onAdd }) {
  const hasMaxParents  = (person.parentIds || []).length >= 2;
  const hasCurrentSpouse =
    (person.marriages || []).some(m => m.status === 'current') ||
    Object.values(people).some(p =>
      p.id !== person.id &&
      (p.marriages || []).some(m => m.spouseId === person.id && m.status === 'current')
    );

  const buttons = [
    !hasMaxParents && {
      mode: 'parent', label: 'Parent',
      cx: node.x, cy: node.y - CARD_HALF_H - BTN_OFFSET,
      labelY: node.y - CARD_HALF_H - BTN_OFFSET - BTN_R - 6,
    },
    {
      mode: 'child', label: 'Child',
      cx: node.x, cy: node.y + CARD_HALF_H + BTN_OFFSET,
      labelY: node.y + CARD_HALF_H + BTN_OFFSET + BTN_R + 12,
    },
    !hasCurrentSpouse && {
      mode: 'spouse', label: 'Spouse',
      cx: node.x + CARD_HALF + BTN_OFFSET, cy: node.y,
      labelY: node.y + BTN_R + 12,
    },
  ].filter(Boolean);

  return (
    <g>
      {buttons.map(({ mode, label, cx, cy, labelY }) => (
        <g
          key={mode}
          onClick={e => { e.stopPropagation(); onAdd(mode); }}
          onMouseDown={e => e.stopPropagation()}
          style={{ cursor: 'pointer' }}
        >
          <title>Add {label}</title>
          <circle cx={cx} cy={cy} r={BTN_R + 4} fill="transparent" />
          <circle cx={cx} cy={cy} r={BTN_R} fill="#3B2F1E" stroke="#C4975A" strokeWidth={1.5} />
          <text x={cx} y={cy + 5} textAnchor="middle" fontSize="16" fill="#C4975A"
            fontWeight="300" fontFamily="'DM Sans', sans-serif" style={{ userSelect: 'none' }}>
            +
          </text>
          <text x={cx} y={labelY} textAnchor="middle" fontSize="9" fill="#6B5E4F"
            fontWeight="700" fontFamily="'DM Sans', sans-serif"
            letterSpacing="0.8" style={{ userSelect: 'none', textTransform: 'uppercase' }}>
            {label}
          </text>
        </g>
      ))}
    </g>
  );
}

// ── Main canvas ────────────────────────────────────────────────────────────
export default function FamilyTreeCanvas({ onSelectPerson, selectedId, canEdit }) {
  const { people } = useTreePeople();
  const { userTrees, activeTreeId, userRole } = useTree();
  const activeTree = userTrees.find(t => t.treeId === activeTreeId);
  const [zoom, setZoom]         = useState(1);
  const [pan, setPan]           = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [containerSize, setContainerSize] = useState({ w: 800, h: 600 });
  const [addRelationMode, setAddRelationMode] = useState(null);
  const [exportMode, setExportMode] = useState(null); // 'png' | 'svg' | null
  const [importing, setImporting] = useState(false);
  const [toolsExpanded, setToolsExpanded] = useState(false);
  const svgRef      = useRef(null);
  const containerRef = useRef(null);
  const hDragRef    = useRef(null);
  const vDragRef    = useRef(null);
  const pinchRef      = useRef(null);
  const zoomRef       = useRef(zoom);
  const panRef        = useRef(pan);
  const dragStartRef  = useRef(dragStart);
  const draggingRef   = useRef(dragging);
  useEffect(() => { panRef.current = pan; });
  useEffect(() => { dragStartRef.current = dragStart; });
  useEffect(() => { draggingRef.current = dragging; });
  useEffect(() => { zoomRef.current = zoom; });

  // Structural hash: captures only topology-relevant fields (parentIds, marriages, birth year).
  // When only a name, photo, or occupation changes, this hash stays the same and buildLayout
  // is not re-run — avoiding an expensive O(n²) recalculation for every profile edit.
  const structuralHash = useMemo(() => {
    return Object.values(people)
      .map(p =>
        `${p.id}:${(p.parentIds||[]).slice().sort().join(',')}` +
        `:${(p.marriages||[]).map(m=>m.spouseId).sort().join(',')}` +
        `:${p.birth||''}`
      )
      .sort()
      .join('|');
  }, [people]);

  const { nodes, familyGroups, remainingLinks, couples } = useMemo(() => buildLayout(people), [structuralHash]); // eslint-disable-line

  const getNode = (id) => nodes.find(n => n.id === id);

  // Root generation = people with no known parents in the tree
  const rootIds = useMemo(() => {
    const s = new Set();
    Object.values(people).forEach(p => {
      const hasParents = p.parentIds && p.parentIds.some(pid => people[pid]);
      if (!hasParents) s.add(p.id);
    });
    return s;
  }, [people]);

  // Track container dimensions — debounced via requestAnimationFrame to prevent
  // 50+ re-renders when the user drags the browser window edge.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    let rafId = null;
    const obs = new ResizeObserver(([entry]) => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const { width, height } = entry.contentRect;
        setContainerSize({ w: width, h: height });
        rafId = null;
      });
    });
    obs.observe(el);
    return () => { obs.disconnect(); if (rafId) cancelAnimationFrame(rafId); };
  }, []);

  // Touch pan/pinch — must be non-passive so e.preventDefault() can block browser scroll/zoom
  useEffect(() => {
    const svgEl = svgRef.current;
    if (!svgEl) return;

    // Two-finger geometry relative to the SVG: finger spread and the midpoint.
    const pinchGeometry = (touches) => {
      const rect = svgEl.getBoundingClientRect();
      const [a, b] = [touches[0], touches[1]];
      return {
        dist: Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY),
        mid:  { x: (a.clientX + b.clientX) / 2 - rect.left, y: (a.clientY + b.clientY) / 2 - rect.top },
      };
    };

    const onTouchStart = (e) => {
      if (e.touches.length === 2) {
        e.preventDefault();
        setDragging(false);
        // Snapshot the gesture's starting state. Every move is computed from
        // this snapshot, never from the live zoom: scaling the live zoom by the
        // start-relative spread compounded on each touchmove, so a small pinch
        // raced to the zoom limit.
        const { dist, mid } = pinchGeometry(e.touches);
        if (dist < 1) return;
        const z = zoomRef.current, p = panRef.current;
        pinchRef.current = {
          dist,
          zoom: z,
          // Tree coordinate under the fingers' midpoint; it stays under them.
          anchor: { x: (mid.x - p.x) / z, y: (mid.y - p.y) / z },
        };
        return;
      }
      if (e.touches.length === 1) {
        if (e.target.closest('g[style]')) return; // card tap — let click fire
        e.preventDefault();
        const t = e.touches[0];
        setDragging(true);
        setDragStart({ x: t.clientX - panRef.current.x, y: t.clientY - panRef.current.y });
      }
    };

    const onTouchMove = (e) => {
      e.preventDefault();
      if (e.touches.length === 1 && draggingRef.current) {
        const t = e.touches[0];
        setPan({ x: t.clientX - dragStartRef.current.x, y: t.clientY - dragStartRef.current.y });
      } else if (e.touches.length === 2 && pinchRef.current) {
        const start = pinchRef.current;
        const { dist, mid } = pinchGeometry(e.touches);
        const newZoom = Math.max(0.25, Math.min(2.5, start.zoom * (dist / start.dist)));
        setZoom(newZoom);
        setPan({ x: mid.x - start.anchor.x * newZoom, y: mid.y - start.anchor.y * newZoom });
      }
    };

    const onTouchEnd = () => {
      setDragging(false);
      pinchRef.current = null;
    };

    svgEl.addEventListener('touchstart', onTouchStart, { passive: false });
    svgEl.addEventListener('touchmove',  onTouchMove,  { passive: false });
    svgEl.addEventListener('touchend',   onTouchEnd,   { passive: false });
    return () => {
      svgEl.removeEventListener('touchstart', onTouchStart);
      svgEl.removeEventListener('touchmove',  onTouchMove);
      svgEl.removeEventListener('touchend',   onTouchEnd);
    };
  }, []); // eslint-disable-line

  // Wheel zoom, attached here for the same reason as the touch handlers above:
  // React registers onWheel at the root as a *passive* listener, so a
  // preventDefault() inside a synthetic wheel handler is ignored and Chrome
  // logs "Unable to preventDefault inside passive event listener invocation"
  // on every tick. Zooming still worked, which is why it went unnoticed — what
  // did not work was suppressing the browser's own gesture underneath it, so a
  // trackpad pinch could zoom the whole page while the canvas zoomed too.
  //
  // No refs needed: the only state it touches goes through a functional
  // updater, so the empty dependency list cannot make it stale.
  useEffect(() => {
    const svgEl = svgRef.current;
    if (!svgEl) return;

    const onWheel = (e) => {
      e.preventDefault();
      setZoom(z => Math.max(0.25, Math.min(2.5, z - e.deltaY * 0.001)));
    };

    svgEl.addEventListener('wheel', onWheel, { passive: false });
    return () => svgEl.removeEventListener('wheel', onWheel);
  }, []);

  // Tree bounding box in tree coordinates (with padding)
  const treeBounds = useMemo(() => {
    if (nodes.length === 0) return { left: -400, right: 400, top: -200, bottom: 200 };
    const xs = nodes.map(n => n.x);
    const ys = nodes.map(n => n.y);
    return {
      left:   Math.min(...xs) - CARD_HALF   - TREE_PADDING,
      right:  Math.max(...xs) + CARD_HALF   + TREE_PADDING,
      top:    Math.min(...ys) - CARD_HALF_H - TREE_PADDING,
      bottom: Math.max(...ys) + CARD_HALF_H + TREE_PADDING,
    };
  }, [nodes]);

  // Viewport culling: only render person cards visible in the current viewport.
  // Buffer of 2 card-widths + one generation gap prevents cards from popping in during panning.
  // Line connectors are left unculled — they're simple SVG primitives without child components.
  const visibleNodes = useMemo(() => {
    if (nodes.length === 0) return nodes;
    const bufX = CARD_W * 2;
    const bufY = V_GAP;
    const left   = (-pan.x) / zoom - bufX;
    const right  = (-pan.x + containerSize.w) / zoom + bufX;
    const top    = (-pan.y) / zoom - bufY;
    const bottom = (-pan.y + containerSize.h) / zoom + bufY;
    return nodes.filter(n =>
      n.x + CARD_HALF > left  && n.x - CARD_HALF < right &&
      n.y + CARD_HALF_H > top && n.y - CARD_HALF_H < bottom
    );
  }, [nodes, pan, zoom, containerSize]);

  // During export, render ALL nodes (not just visible) so the clone contains the full tree
  const renderNodes = exportMode ? nodes : visibleNodes;

  // Fit all nodes in the viewport
  const fitToView = useCallback(() => {
    if (nodes.length === 0) return;
    const xs = nodes.map(n => n.x);
    const ys = nodes.map(n => n.y);
    const minX = Math.min(...xs) - CARD_HALF   - 40;
    const maxX = Math.max(...xs) + CARD_HALF   + 40;
    const minY = Math.min(...ys) - CARD_HALF_H - 40;
    const maxY = Math.max(...ys) + CARD_HALF_H + 40;
    const tW = maxX - minX;
    const tH = maxY - minY;
    const { w: vw, h: vh } = containerSize;
    const newZoom = Math.max(0.25, Math.min(2, Math.min(vw / tW, vh / tH) * 0.9));
    setZoom(newZoom);
    setPan({
      x: (vw - tW * newZoom) / 2 - minX * newZoom,
      y: (vh - tH * newZoom) / 2 - minY * newZoom,
    });
  }, [nodes, containerSize]);

  // ── Scrollbar maths ────────────────────────────────────────────────────────
  const { w: vw, h: vh } = containerSize;
  const treeW = (treeBounds.right  - treeBounds.left) * zoom;
  const treeH = (treeBounds.bottom - treeBounds.top)  * zoom;

  // Horizontal
  const hTrackW     = vw - SCROLL_SIZE;
  const hThumbW     = Math.max(THUMB_MIN, hTrackW * Math.min(1, vw / treeW));
  const showHScroll = treeW > vw;
  const panXMax     = -(treeBounds.left  * zoom);           // pan.x when tree left edge == viewport left
  const panXMin     = -(treeBounds.right * zoom - vw);      // pan.x when tree right edge == viewport right
  const panXRange   = panXMax - panXMin;
  const hFrac       = panXRange > 0 ? Math.max(0, Math.min(1, (panXMax - pan.x) / panXRange)) : 0;
  const hThumbLeft  = hFrac * (hTrackW - hThumbW);

  // Vertical
  const vTrackH     = vh - SCROLL_SIZE;
  const vThumbH     = Math.max(THUMB_MIN, vTrackH * Math.min(1, vh / treeH));
  const showVScroll = treeH > vh;
  const panYMax     = -(treeBounds.top    * zoom);
  const panYMin     = -(treeBounds.bottom * zoom - vh);
  const panYRange   = panYMax - panYMin;
  const vFrac       = panYRange > 0 ? Math.max(0, Math.min(1, (panYMax - pan.y) / panYRange)) : 0;
  const vThumbTop   = vFrac * (vTrackH - vThumbH);

  // Scrollbar thumb drag handlers
  const handleHThumbMouseDown = (e) => {
    e.preventDefault(); e.stopPropagation();
    const startFrac = hFrac;
    const startX    = e.clientX;
    const travelW   = hTrackW - hThumbW;
    const onMove = (me) => {
      const newFrac  = Math.max(0, Math.min(1, startFrac + (me.clientX - startX) / travelW));
      setPan(p => ({ ...p, x: panXMax - newFrac * panXRange }));
    };
    const onUp = () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const handleVThumbMouseDown = (e) => {
    e.preventDefault(); e.stopPropagation();
    const startFrac = vFrac;
    const startY    = e.clientY;
    const travelH   = vTrackH - vThumbH;
    const onMove = (me) => {
      const newFrac  = Math.max(0, Math.min(1, startFrac + (me.clientY - startY) / travelH));
      setPan(p => ({ ...p, y: panYMax - newFrac * panYRange }));
    };
    const onUp = () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // Click on scrollbar track (jump to position)
  const handleHTrackClick = (e) => {
    const rect    = e.currentTarget.getBoundingClientRect();
    const clickX  = e.clientX - rect.left;
    const newFrac = Math.max(0, Math.min(1, (clickX - hThumbW / 2) / (hTrackW - hThumbW)));
    setPan(p => ({ ...p, x: panXMax - newFrac * panXRange }));
  };

  const handleVTrackClick = (e) => {
    const rect    = e.currentTarget.getBoundingClientRect();
    const clickY  = e.clientY - rect.top;
    const newFrac = Math.max(0, Math.min(1, (clickY - vThumbH / 2) / (vTrackH - vThumbH)));
    setPan(p => ({ ...p, y: panYMax - newFrac * panYRange }));
  };

  const handleMouseDown = (e) => {
    if (e.target.closest('g[style]')) return;
    setDragging(true);
    setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
  };
  const handleMouseMove = (e) => {
    if (dragging) setPan({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
  };
  const handleMouseUp = () => setDragging(false);

  const isEmpty = Object.keys(people).length === 0;

  // After exportMode is set, a re-render puts all nodes in the DOM; then clone and export.
  useEffect(() => {
    if (!exportMode || !svgRef.current || nodes.length === 0) return;

    const NS = 'http://www.w3.org/2000/svg';
    const { left, top, right, bottom } = treeBounds;
    const tw = right - left;
    const th = bottom - top;

    const clone = svgRef.current.cloneNode(true);
    clone.setAttribute('viewBox', `${left} ${top} ${tw} ${th}`);
    clone.setAttribute('xmlns', NS);
    clone.removeAttribute('style');

    const innerG = clone.firstElementChild;
    if (innerG) {
      innerG.removeAttribute('transform');
      const bg = document.createElementNS(NS, 'rect');
      bg.setAttribute('x', String(left)); bg.setAttribute('y', String(top));
      bg.setAttribute('width', String(tw)); bg.setAttribute('height', String(th));
      bg.setAttribute('fill', '#FDFBF7');
      innerG.insertBefore(bg, innerG.firstChild);
    }

    clone.querySelectorAll('[data-no-export]').forEach(el => el.remove());

    // Replace <image> (signed-URL photos taint the canvas) with initials text
    clone.querySelectorAll('image').forEach(imgEl => {
      const m = (imgEl.getAttribute('clipPath') || '').match(/url\(#clip-(.+?)\)/);
      const person = m ? people[m[1]] : null;
      if (person) {
        const txt = document.createElementNS(NS, 'text');
        txt.setAttribute('x', '-52'); txt.setAttribute('y', '5');
        txt.setAttribute('text-anchor', 'middle');
        txt.setAttribute('font-size', '12'); txt.setAttribute('font-weight', '700');
        txt.setAttribute('fill', '#6B5E4F');
        txt.setAttribute('font-family', "'Crimson Pro', Georgia, serif");
        txt.textContent = getInitials(person.name);
        imgEl.parentNode.insertBefore(txt, imgEl);
      }
      imgEl.remove();
    });
    clone.querySelectorAll('clipPath').forEach(el => el.remove());

    const svgStr = new XMLSerializer().serializeToString(clone);

    if (exportMode === 'svg') {
      const a = document.createElement('a');
      a.download = 'family-tree.svg';
      a.href = URL.createObjectURL(new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' }));
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
      setExportMode(null);
      return;
    }

    // PNG: scale up for high-DPI output; cap at 16000px to stay within browser canvas limits
    const scale = Math.min(3, 16000 / Math.max(tw, th));
    const outW = Math.round(tw * scale);
    const outH = Math.round(th * scale);
    clone.setAttribute('width', String(outW));
    clone.setAttribute('height', String(outH));

    const url = URL.createObjectURL(new Blob([new XMLSerializer().serializeToString(clone)], { type: 'image/svg+xml;charset=utf-8' }));
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = outW; canvas.height = outH;
      canvas.getContext('2d').drawImage(img, 0, 0);
      URL.revokeObjectURL(url);
      const a = document.createElement('a');
      a.download = 'family-tree.png';
      a.href = canvas.toDataURL('image/png');
      a.click();
      setExportMode(null);
    };
    img.onerror = () => { URL.revokeObjectURL(url); setExportMode(null); };
    img.src = url;
  }, [exportMode]); // eslint-disable-line

  return (
    <div ref={containerRef} style={{ flex: 1, position: 'relative', overflow: 'hidden', background: '#FDFBF7' }}>
      {/* Dot grid */}
      <div style={{
        position: 'absolute', inset: 0, opacity: 0.35, pointerEvents: 'none',
        backgroundImage: 'radial-gradient(circle, #C4975A 0.5px, transparent 0.5px)',
        backgroundSize: '28px 28px',
      }} />

      {/* Zoom controls — collapsed by default; the toggle stays the bottom-most
          child so its on-screen position doesn't shift when expanding/collapsing. */}
      <div style={{
        position: 'absolute', bottom: 24, right: 24, zIndex: 10,
        display: 'flex', flexDirection: 'column', gap: 6,
        background: '#fff', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
        padding: 6, border: '1px solid #E8E0D4',
      }}>
        {toolsExpanded && (
          <>
            <button onClick={() => setZoom(z => Math.min(z + 0.15, 2.5))} style={zoomBtn}><Icons.ZoomIn /></button>
            <button onClick={() => setZoom(z => Math.max(z - 0.15, 0.25))} style={zoomBtn}><Icons.ZoomOut /></button>
            <div style={{ height: 1, background: '#E8E0D4', margin: '2px 4px' }} />
            <button onClick={fitToView} style={zoomBtn} title="Fit all"><Icons.Reset /></button>
            <div style={{ height: 1, background: '#E8E0D4', margin: '2px 4px' }} />
            <button
              onClick={() => { if (!exportMode && !isEmpty) setExportMode('png'); }}
              style={{ ...zoomBtn, opacity: (exportMode || isEmpty) ? 0.45 : 1, cursor: (exportMode || isEmpty) ? 'default' : 'pointer', flexDirection: 'column', gap: 1, fontSize: 8, fontWeight: 600, color: '#6B5E4F' }}
              title="Export as PNG (high resolution)"
            >
              <Icons.Download size={13} />
              PNG
            </button>
            <button
              onClick={() => { if (!exportMode && !isEmpty) setExportMode('svg'); }}
              style={{ ...zoomBtn, opacity: (exportMode || isEmpty) ? 0.45 : 1, cursor: (exportMode || isEmpty) ? 'default' : 'pointer', flexDirection: 'column', gap: 1, fontSize: 8, fontWeight: 600, color: '#6B5E4F' }}
              title="Export as SVG (scalable vector, zoom without blur)"
            >
              <Icons.Download size={13} />
              SVG
            </button>
            {userRole === 'owner' && (
              <button
                onClick={() => downloadGedcom(people, activeTree?.name)}
                style={{ ...zoomBtn, flexDirection: 'column', gap: 1, fontSize: 8, fontWeight: 600, color: '#6B5E4F' }}
                title="Export as GEDCOM (genealogy file format)"
              >
                <Icons.Download size={13} />
                GEDCOM
              </button>
            )}
            {userRole === 'owner' && (
              <button
                onClick={() => setImporting(true)}
                style={{ ...zoomBtn, flexDirection: 'column', gap: 1, fontSize: 8, fontWeight: 600, color: '#6B5E4F' }}
                title="Import a GEDCOM file from another genealogy program"
              >
                <Icons.Upload size={13} />
                IMPORT
              </button>
            )}
            <div style={{ height: 1, background: '#E8E0D4', margin: '2px 4px' }} />
          </>
        )}
        <button
          onClick={() => setToolsExpanded(v => !v)}
          style={zoomBtn}
          title={toolsExpanded ? 'Collapse tree tools' : 'Tree tools (zoom, fit, export)'}
        >
          <span style={{ display: 'flex', transform: toolsExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>
            <Icons.ChevronDown size={14} />
          </span>
        </button>
      </div>

      {/* Empty state */}
      {isEmpty && (
        <div style={{
          position: 'absolute', inset: 0, display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 5,
          flexDirection: 'column', gap: 12,
        }}>
          <div style={{
            width: 80, height: 80, borderRadius: '50%',
            background: '#F5EDE3', display: 'flex', alignItems: 'center',
            justifyContent: 'center', color: '#C4975A',
          }}>
            <Icons.Tree size={36} />
          </div>
          <h3 style={{
            margin: 0, fontFamily: "'Crimson Pro', Georgia, serif",
            fontSize: 22, color: '#2C2418', fontWeight: 700,
          }}>Your family tree is empty</h3>
          <p style={{ margin: 0, fontSize: 14, color: '#8B7D6B' }}>
            Click "Add Person" in the sidebar to get started
          </p>
          {/* An empty tree is exactly when someone has a file from another
              program to bring across, so the offer belongs here and not only
              behind the collapsed toolbar. */}
          {userRole === 'owner' && (
            <button
              onClick={() => setImporting(true)}
              style={{
                marginTop: 4, padding: '9px 18px', borderRadius: 8,
                border: '1.5px solid #D5CAB8', background: '#fff', color: '#6B5E4F',
                fontSize: 13, fontWeight: 600, cursor: 'pointer',
                fontFamily: "'DM Sans', sans-serif",
                display: 'flex', alignItems: 'center', gap: 7,
              }}
            >
              <Icons.Upload size={14} />
              Import a GEDCOM file
            </button>
          )}
        </div>
      )}

      <svg
        ref={svgRef}
        width="100%" height="100%"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        style={{ cursor: dragging ? 'grabbing' : 'grab' }}
      >
        <g transform={`translate(${pan.x}, ${pan.y}) scale(${zoom})`}>

          {/* ── Couple marriage lines ─────────────────────────────────────── */}
          {couples.map((c, i) => {
            const na = getNode(c.a);
            const nb = getNode(c.b);
            if (!na || !nb) return null;
            // Skip if spouses ended up at different generations (mis-positioned)
            if (Math.abs(na.y - nb.y) > 10) return null;
            // Ensure left→right order for the connector
            const left  = na.x <= nb.x ? na : nb;
            const right = na.x <= nb.x ? nb : na;
            const lineX1 = left.x  + CARD_HALF;
            const lineX2 = right.x - CARD_HALF;
            const lineY  = left.y; // both spouses share the same Y
            const midX   = (left.x + right.x) / 2;
            const eitherDeceased = !!(people[c.a]?.death || people[c.b]?.death);
            const isCurrent  = c.status === 'current' && !eitherDeceased;
            const isDivorced = c.status === 'ended' && (c.endReason === 'divorce' || c.endReason === 'annulment');

            return (
              <g key={`couple-${i}`}>
                <line
                  x1={lineX1} y1={lineY} x2={lineX2} y2={lineY}
                  stroke={isDivorced ? '#D4A574' : isCurrent ? '#C4975A' : '#B0A090'}
                  strokeWidth={isCurrent ? 2 : 1.5}
                  strokeDasharray={!isCurrent ? '4,6' : undefined}
                  opacity={isDivorced ? 0.5 : 0.8}
                />
                {isDivorced && (
                  <g transform={`translate(${midX}, ${lineY})`}>
                    <circle r={10} fill="#FFF3E0" stroke="#D4A574" strokeWidth={1.5} />
                    <line x1={-4} y1={-4} x2={4} y2={4} stroke="#BF6C00" strokeWidth={1.5} strokeLinecap="round" />
                    <line x1={4} y1={-4} x2={-4} y2={4} stroke="#BF6C00" strokeWidth={1.5} strokeLinecap="round" />
                  </g>
                )}
                {isCurrent && (
                  <g transform={`translate(${midX}, ${lineY})`}>
                    <circle r={8} fill="#E8F5E9" stroke="#6DAE72" strokeWidth={1.5} />
                    <text textAnchor="middle" y={4} fontSize="9" fill="#4A7C4F" fontWeight="700">♥</text>
                  </g>
                )}
              </g>
            );
          })}

          {/* ── Family group T-connectors (couple/parent → children) ──────── */}
          {(() => {
            // Pre-compute stagger rank: when multiple families share the same parent
            // at the same child Y level, stagger their junctionY upward so the
            // horizontal bars sit at different heights and never visually cross.
            const parentLevelMap = {}; // "parentId|childY" → [groupIndex, ...]
            familyGroups.forEach((group, gi) => {
              const firstChild = group.children.map(id => getNode(id)).find(Boolean);
              if (!firstChild) return;
              const cy = Math.round(firstChild.y);
              group.parents.forEach(pid => {
                const key = `${pid}|${cy}`;
                if (!parentLevelMap[key]) parentLevelMap[key] = [];
                if (!parentLevelMap[key].includes(gi)) parentLevelMap[key].push(gi);
              });
            });
            const groupStagger = {}; // groupIndex → stagger rank (0 = primary, 1+ = secondary)
            Object.values(parentLevelMap).forEach(gis => {
              if (gis.length <= 1) return;
              gis.forEach((gi, rank) => { groupStagger[gi] = Math.max(groupStagger[gi] || 0, rank); });
            });
            const STAGGER_STEP = 18; // px per rank — enough to visually separate bars

            // Second stagger pass: families from DIFFERENT parents (e.g. two brothers
            // each with multiple wives) can still end up at the same childY with bars that
            // overlap in X.  The shared-parent pass above won't catch them, so we
            // explicitly check every pair at the same childY for X-range overlap.
            {
              // Compute each group's bar X range (dropX + child positions)
              const barRanges = {}; // gi → { leftX, rightX, cy }
              familyGroups.forEach((group, gi) => {
                const na = getNode(group.parents[0]);
                const nb = group.parents[1] ? getNode(group.parents[1]) : null;
                if (!na) return;
                const allChildNodes = group.children.map(id => getNode(id)).filter(Boolean);
                if (!allChildNodes.length) return;
                const cy = Math.round(allChildNodes[0].y);
                const coupleDropX = nb
                  ? (group.dropParentId ? (getNode(group.dropParentId)?.x ?? (na.x + nb.x) / 2) : (na.x + nb.x) / 2)
                  : na.x;
                const singleParentMap = group.childSingleParent || {};
                const sharedNodes = allChildNodes.filter(cn => !singleParentMap[cn.id]);
                let minX = coupleDropX, maxX = coupleDropX;
                sharedNodes.forEach(cn => { minX = Math.min(minX, cn.x); maxX = Math.max(maxX, cn.x); });
                Object.entries(singleParentMap).forEach(([cid, pid]) => {
                  const cn = getNode(cid);
                  const pn = getNode(pid);
                  const dx = pn ? pn.x : coupleDropX;
                  if (cn) { minX = Math.min(minX, dx, cn.x); maxX = Math.max(maxX, dx, cn.x); }
                });
                barRanges[gi] = { leftX: minX, rightX: maxX, cy };
              });

              // Group by childY then resolve overlapping pairs
              const byCy = {};
              Object.entries(barRanges).forEach(([gi, r]) => {
                if (!byCy[r.cy]) byCy[r.cy] = [];
                byCy[r.cy].push(Number(gi));
              });
              Object.values(byCy).forEach(gis => {
                if (gis.length <= 1) return;
                // Sort by leftX so we process left-to-right
                gis.sort((a, b) => (barRanges[a]?.leftX ?? 0) - (barRanges[b]?.leftX ?? 0));
                for (let i = 1; i < gis.length; i++) {
                  const ri = barRanges[gis[i]];
                  if (!ri) continue;
                  for (let j = 0; j < i; j++) {
                    const rj = barRanges[gis[j]];
                    if (!rj) continue;
                    if (rj.leftX < ri.rightX && ri.leftX < rj.rightX) {
                      // Bars overlap in X — gis[i] must have a higher rank than gis[j]
                      if ((groupStagger[gis[i]] || 0) <= (groupStagger[gis[j]] || 0)) {
                        groupStagger[gis[i]] = (groupStagger[gis[j]] || 0) + 1;
                      }
                    }
                  }
                }
              });
            }

            return familyGroups.map((group, i) => {
            const [aId, bId] = group.parents;
            const na = getNode(aId);
            const nb = bId ? getNode(bId) : null;
            if (!na) return null;

            const allChildNodes = group.children.map(id => getNode(id)).filter(Boolean);
            if (allChildNodes.length === 0) return null;

            const parentY   = na.y;
            const childY    = allChildNodes[0].y;
            const staggerRank = groupStagger[i] || 0;
            const junctionY = parentY + (childY - parentY) * 0.5 - staggerRank * STAGGER_STEP;
            // For secondary families the T-connector drops from the secondary spouse's
            // card so that children appear directly below their parent cluster, not
            // below the midpoint of a very long marriage line.
            const coupleDropX = nb
              ? (group.dropParentId ? (getNode(group.dropParentId)?.x ?? (na.x + nb.x) / 2) : (na.x + nb.x) / 2)
              : na.x;
            const singleParentMap = group.childSingleParent || {};

            // Split children into: shared (couple midpoint drop) vs per-parent (single-parent drop)
            const sharedNodes = allChildNodes.filter(cn => !singleParentMap[cn.id]);
            const byParent = {};
            allChildNodes.filter(cn => singleParentMap[cn.id]).forEach(cn => {
              const pid = singleParentMap[cn.id];
              if (!byParent[pid]) byParent[pid] = [];
              byParent[pid].push(cn);
            });

            // Build one connector segment per drop source.
            const segments = [];
            if (sharedNodes.length > 0) {
              // Shared children: the drop comes from coupleDropX straight down to the bar.
              // Include coupleDropX in the bar range so the drop always connects to the bar
              // even when children are positioned off-centre (e.g. Phase-4 anchor-parent layout).
              const xs = sharedNodes.map(n => n.x);
              segments.push({ dropX: coupleDropX, nodes: sharedNodes,
                leftX: Math.min(coupleDropX, ...xs), rightX: Math.max(coupleDropX, ...xs) });
            }
            Object.entries(byParent).forEach(([pid, nodes]) => {
              // Single-parent children: drop comes from that parent's card.
              // Extend the bar to include dropX so it bridges the gap when the child's
              // slot centre doesn't align with the parent (common for step-children).
              const pNode = getNode(pid);
              const dropX = pNode ? pNode.x : coupleDropX;
              const xs = nodes.map(n => n.x);
              segments.push({ dropX, nodes,
                leftX: Math.min(dropX, ...xs), rightX: Math.max(dropX, ...xs) });
            });

            // Use distinct color when this parent has children with multiple spouses,
            // otherwise fall back to the default neutral line color.
            const lineColor = group.color || '#D5CAB8';
            const dotFill   = group.color || '#C4975A';
            const dotStroke = group.color ? '#00000033' : '#8B6230';

            return (
              <g key={`family-${i}`}>
                {segments.map((seg, si) => (
                  <g key={si}>
                    {/* Vertical drop from parent/couple to junction */}
                    <line x1={seg.dropX} y1={parentY + CARD_HALF_H} x2={seg.dropX} y2={junctionY}
                      stroke={lineColor} strokeWidth={1.8} />
                    {/* Horizontal bar */}
                    <line x1={seg.leftX} y1={junctionY} x2={seg.rightX} y2={junctionY}
                      stroke={lineColor} strokeWidth={1.8} />
                    {/* Vertical drops to each child's top */}
                    {seg.nodes.map(cn => (
                      <line key={cn.id} x1={cn.x} y1={junctionY} x2={cn.x} y2={cn.y - CARD_HALF_H}
                        stroke={lineColor} strokeWidth={1.8} />
                    ))}
                    {/* Union node: a small dot on the marriage line anchoring the drop to this
                        specific couple, making it unambiguous which union the children belong to */}
                    {nb && !singleParentMap[seg.nodes[0]?.id] && (
                      <circle cx={seg.dropX} cy={parentY} r={5}
                        fill={dotFill} stroke={dotStroke} strokeWidth={1} opacity={0.9} />
                    )}
                  </g>
                ))}
              </g>
            );
          });
          })()}

          {/* ── Fallback curved links for unmatched parent–child pairs ──────── */}
          {remainingLinks.map((l, i) => {
            const from = getNode(l.from);
            const to   = getNode(l.to);
            if (!from || !to) return null;
            const midY = (from.y + to.y) / 2;
            return (
              <path key={`link-${i}`}
                d={`M${from.x},${from.y + CARD_HALF_H} C${from.x},${midY} ${to.x},${midY} ${to.x},${to.y - CARD_HALF_H}`}
                fill="none" stroke="#D5CAB8" strokeWidth={1.8}
              />
            );
          })}

          {/* ── Person cards (rendered on top of lines) ───────────────────── */}
          {renderNodes.map(n => {
            const person = people[n.id];
            if (!person) return null;
            return (
              <TreePersonCard
                key={n.id}
                person={person}
                x={n.x} y={n.y}
                isRoot={rootIds.has(n.id)}
                isSelected={selectedId === n.id}
                onSelect={onSelectPerson}
              />
            );
          })}

          {/* ── Relationship add buttons for selected card ────────────────── */}
          {canEdit && selectedId && (() => {
            const selNode   = nodes.find(n => n.id === selectedId);
            const selPerson = people[selectedId];
            if (!selNode || !selPerson) return null;
            return (
              <g data-no-export="true">
                <RelationshipButtons
                  node={selNode}
                  person={selPerson}
                  people={people}
                  onAdd={mode => setAddRelationMode(mode)}
                />
              </g>
            );
          })()}
        </g>
      </svg>

      {importing && <ImportGedcomModal onClose={() => setImporting(false)} />}

      {addRelationMode && (
        <AddRelatedPersonModal
          relationshipType={addRelationMode}
          anchorPerson={people[selectedId]}
          onClose={() => setAddRelationMode(null)}
        />
      )}

      {/* ── Horizontal scrollbar ─────────────────────────────────────────── */}
      {showHScroll && (
        <div
          onClick={handleHTrackClick}
          style={{
            position: 'absolute', bottom: 0, left: 0,
            width: vw - SCROLL_SIZE, height: SCROLL_SIZE,
            background: 'rgba(196,151,90,0.08)',
            borderTop: '1px solid rgba(196,151,90,0.15)',
          }}
        >
          <div
            onMouseDown={handleHThumbMouseDown}
            onClick={e => e.stopPropagation()}
            style={{
              position: 'absolute', top: 2, height: SCROLL_SIZE - 4,
              left: hThumbLeft, width: hThumbW,
              background: 'rgba(196,151,90,0.45)', borderRadius: 4,
              cursor: 'grab', transition: 'background 0.15s',
            }}
          />
        </div>
      )}

      {/* ── Vertical scrollbar ───────────────────────────────────────────── */}
      {showVScroll && (
        <div
          onClick={handleVTrackClick}
          style={{
            position: 'absolute', top: 0, right: 0,
            width: SCROLL_SIZE, height: vh - SCROLL_SIZE,
            background: 'rgba(196,151,90,0.08)',
            borderLeft: '1px solid rgba(196,151,90,0.15)',
          }}
        >
          <div
            onMouseDown={handleVThumbMouseDown}
            onClick={e => e.stopPropagation()}
            style={{
              position: 'absolute', left: 2, width: SCROLL_SIZE - 4,
              top: vThumbTop, height: vThumbH,
              background: 'rgba(196,151,90,0.45)', borderRadius: 4,
              cursor: 'grab', transition: 'background 0.15s',
            }}
          />
        </div>
      )}

      {/* Corner fill when both scrollbars visible */}
      {showHScroll && showVScroll && (
        <div style={{
          position: 'absolute', bottom: 0, right: 0,
          width: SCROLL_SIZE, height: SCROLL_SIZE,
          background: 'rgba(196,151,90,0.08)',
        }} />
      )}
    </div>
  );
}

const zoomBtn = {
  width: 36, height: 36, borderRadius: 8, border: 'none',
  background: 'transparent', cursor: 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  color: '#6B5E4F', fontSize: 14,
};
